import { PlaceIntradayPositionsPayload, usePlaceIntradayPositionsMutation } from 'api/elplan/elplan.api'
import { LATEST_BID_SUBMISSIONS_QUERY_KEY } from 'api/elplan/elplan.constants'
import { resolveElplanErrorAlert } from 'api/elplan/elplan.errorAlert.helper'
import { getElplanValidationError } from 'api/elplan/elplan.errorBody.helper'
import type { ElplanInfo } from 'api/elplan/elplan.types'
import { queryClient } from 'helpers/queryClient'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import type { IntradayBidPositionsPayload } from '../../IntradayTradingView.types'

function hasDirectionalTagConfig(tag: {
  tag_id: string
  display_name: string
  enabled?: boolean
  buy: Record<string, unknown>
  sell: Record<string, unknown>
}): boolean {
  return Boolean(
    tag.buy &&
      tag.sell &&
      typeof tag.buy === 'object' &&
      !Array.isArray(tag.buy) &&
      typeof tag.sell === 'object' &&
      !Array.isArray(tag.sell)
  )
}

type UseIntradayBidSubmissionParams = {
  systemId: number | undefined
  elplanInfo: ElplanInfo | undefined
}

export function useIntradayBidSubmission({ systemId, elplanInfo }: UseIntradayBidSubmissionParams) {
  const { t } = useTranslation()
  const { mutateAsync: placeIntradayPositions, isPending: isSubmitting } = usePlaceIntradayPositionsMutation()

  const configuredPositions = (elplanInfo?.intraday_configuration?.positions ?? []).map((position) => ({
    position_id: position.position_id,
    display_name: position.display_name,
    tags: (position.tags ?? []).filter((tag) => hasDirectionalTagConfig(tag)),
  }))
  const hasIntradayConfig = configuredPositions.some((position) => position.tags.length > 0)

  async function handleSubmitPositions(positions: IntradayBidPositionsPayload): Promise<number | undefined> {
    if (!systemId) {
      alertStore.error(t('Cannot submit positions because no active system is selected.'), {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: null,
      })
      return
    }

    const payload: PlaceIntradayPositionsPayload = {
      systemId,
      positions,
    }

    alertStore.info(t('Positions submitted.'), {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      autoHideDuration: 6000,
    })

    try {
      const result = await placeIntradayPositions(payload)

      await queryClient.invalidateQueries({
        queryKey: [LATEST_BID_SUBMISSIONS_QUERY_KEY, { systemId }],
      })

      if (result?.unmapped_series_ids && result.unmapped_series_ids.length > 0) {
        alertStore.warning(
          t('Positions submitted, but some series could not be mapped: {{series}}', {
            series: result.unmapped_series_ids.join(', '),
          }),
          {
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            autoHideDuration: null,
          }
        )
      }

      return result?.batch_id
    } catch (error) {
      const validationMessage = getElplanValidationError(error)
      const alert = resolveElplanErrorAlert(error, {
        operation: 'send',
        fallback: validationMessage ? { key: validationMessage } : { key: 'Failed to submit intraday positions.' },
      })

      alertStore.error(
        t(alert.key, alert.values),
        {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: null,
        },
        alert.alertKey
      )
    }
  }

  return {
    configuredPositions,
    hasIntradayConfig,
    isSubmitting,
    handleSubmitPositions,
  }
}
